import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { 
  Upload, 
  User, 
  Palette, 
  Check,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Sparkles,
  X,
  ImagePlus,
  Package,
  Layers
} from "lucide-react";

const BulkUpload = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Step 1: Multiple Product Uploads
  const [products, setProducts] = useState([]);
  const [productCategory, setProductCategory] = useState("fashion");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Step 2: Model Selection
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelFilters, setModelFilters] = useState({
    gender: "",
    age_group: "",
    complexion: "",
    hair: ""
  });
  
  // Step 3: Background & Pose
  const [backgrounds, setBackgrounds] = useState([]);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [selectedPoses, setSelectedPoses] = useState(["front_pose"]);
  const [videoRequired, setVideoRequired] = useState(true);
  const [bgCategory, setBgCategory] = useState("");
  
  // Created SKU IDs
  const [createdSkuIds, setCreatedSkuIds] = useState([]);

  useEffect(() => {
    if (step === 2) fetchModels();
    if (step === 3) fetchBackgrounds();
  }, [step, modelFilters, bgCategory]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (modelFilters.gender) params.append("gender", modelFilters.gender);
      if (modelFilters.age_group) params.append("age_group", modelFilters.age_group);
      if (modelFilters.complexion) params.append("complexion", modelFilters.complexion);
      if (modelFilters.hair) params.append("hair", modelFilters.hair);
      
      const response = await axios.get(`${API}/models?${params}`, { withCredentials: true });
      setModels(response.data);
    } catch (error) {
      toast.error("Failed to load models");
    } finally {
      setLoading(false);
    }
  };

  const fetchBackgrounds = async () => {
    setLoading(true);
    try {
      const params = bgCategory ? `?category=${bgCategory}` : "";
      const response = await axios.get(`${API}/backgrounds${params}`, { withCredentials: true });
      setBackgrounds(response.data);
    } catch (error) {
      toast.error("Failed to load backgrounds");
    } finally {
      setLoading(false);
    }
  };

  const handleFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const imageFiles = files.filter(f => f.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      toast.error("Please select image files");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    const newProducts = [];
    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        const productName = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
        newProducts.push({
          id: `temp_${Date.now()}_${i}`,
          name: productName,
          image_url: response.data.url,
          file_name: file.name
        });
        
        setUploadProgress(Math.round(((i + 1) / imageFiles.length) * 100));
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setProducts(prev => [...prev, ...newProducts]);
    setUploading(false);
    toast.success(`Uploaded ${newProducts.length} images`);
  };

  const removeProduct = (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  const updateProductName = (id, name) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, name } : p));
  };

  const handleStep1Submit = async () => {
    if (products.length === 0) {
      toast.error("Please upload at least one product");
      return;
    }
    
    const emptyNames = products.filter(p => !p.name.trim());
    if (emptyNames.length > 0) {
      toast.error("Please provide names for all products");
      return;
    }
    
    setLoading(true);
    try {
      const skusData = products.map(p => ({
        sku_name: p.name,
        category: productCategory,
        uploaded_image_url: p.image_url
      }));
      
      const response = await axios.post(`${API}/skus/bulk`, { skus: skusData }, { withCredentials: true });
      setCreatedSkuIds(response.data.skus.map(s => s.sku_id));
      toast.success(`Created ${response.data.created} SKUs`);
      setStep(2);
    } catch (error) {
      toast.error("Failed to create SKUs");
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = () => {
    if (!selectedModel) {
      toast.error("Please select a model");
      return;
    }
    setStep(3);
  };

  const handleStep3Submit = () => {
    if (!selectedBackground) {
      toast.error("Please select a background");
      return;
    }
    if (selectedPoses.length === 0) {
      toast.error("Please select at least one pose");
      return;
    }
    setStep(4);
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/shoots/bulk`, {
        sku_ids: createdSkuIds,
        model_id: selectedModel.model_id,
        background_id: selectedBackground.background_id,
        pose_set: selectedPoses,
        video_required: videoRequired
      }, { withCredentials: true });
      
      toast.success(`Started AI generation for ${response.data.created} products!`);
      navigate("/shoots");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start bulk AI shoot");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePose = (pose) => {
    setSelectedPoses(prev => 
      prev.includes(pose) 
        ? prev.filter(p => p !== pose)
        : [...prev, pose]
    );
  };

  const poses = [
    { id: "front_pose", label: "Front Pose" },
    { id: "side_pose", label: "Side Pose" },
    { id: "walk_pose", label: "Walk Pose" },
    { id: "lifestyle_pose", label: "Lifestyle Pose" }
  ];

  const bgCategories = [
    { id: "", label: "All" },
    { id: "indoor", label: "Indoor" },
    { id: "outdoor", label: "Outdoor" },
    { id: "royal", label: "Royal" },
    { id: "nature", label: "Nature" },
    { id: "cosmetic", label: "Cosmetic" }
  ];

  return (
    <DashboardLayout>
      <div data-testid="bulk-upload-page" className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
            <Layers className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-neutral-900">Bulk AI Shoot</h1>
            <p className="text-neutral-600">Process multiple products with the same settings</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${step >= s ? 'bg-violet-600 text-white' : 'bg-neutral-100 text-neutral-400'}`}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 4 && <div className={`w-16 h-1 mx-2 rounded-full transition-colors ${step > s ? 'bg-violet-600' : 'bg-neutral-100'}`} />}
            </div>
          ))}
        </div>

        <Card className="border-neutral-100">
          {/* Step 1: Bulk Upload */}
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <ImagePlus className="w-5 h-5 text-violet-600" />
                  Step 1: Upload Multiple Products
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Category</Label>
                  <div className="flex gap-4 mt-2">
                    <Button type="button" variant={productCategory === "fashion" ? "default" : "outline"} onClick={() => setProductCategory("fashion")} className={productCategory === "fashion" ? "bg-violet-600" : ""}>Fashion</Button>
                    <Button type="button" variant={productCategory === "cosmetic" ? "default" : "outline"} onClick={() => setProductCategory("cosmetic")} className={productCategory === "cosmetic" ? "bg-violet-600" : ""}>Cosmetic</Button>
                  </div>
                </div>

                {/* Upload Area */}
                <div>
                  <Label>Upload Product Images (Select multiple)</Label>
                  <label data-testid="bulk-upload-input" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-neutral-200 rounded-xl cursor-pointer hover:border-violet-400 hover:bg-violet-50/50 transition-colors mt-2">
                    {uploading ? (
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 text-violet-600 animate-spin mx-auto mb-2" />
                        <Progress value={uploadProgress} className="w-48 h-2" />
                        <p className="text-sm text-neutral-600 mt-2">Uploading... {uploadProgress}%</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                        <p className="text-sm text-neutral-600">Click to select multiple images</p>
                        <p className="text-xs text-neutral-400 mt-1">PNG, JPG up to 10MB each</p>
                      </>
                    )}
                    <input type="file" className="hidden" accept="image/*" multiple onChange={handleFilesUpload} disabled={uploading} />
                  </label>
                </div>

                {/* Product List */}
                {products.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label>{products.length} Products</Label>
                      <Button variant="ghost" size="sm" onClick={() => setProducts([])} className="text-red-600 hover:text-red-700">Clear All</Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-80 overflow-y-auto">
                      {products.map((product) => (
                        <div key={product.id} className="relative bg-neutral-50 rounded-xl p-2">
                          <button onClick={() => removeProduct(product.id)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm z-10 hover:bg-red-600">
                            <X className="w-4 h-4" />
                          </button>
                          <img src={product.image_url.startsWith('/') ? `${window.location.origin}${product.image_url}` : product.image_url} alt={product.name} className="w-full aspect-square object-cover rounded-lg mb-2" />
                          <Input value={product.name} onChange={(e) => updateProductName(product.id, e.target.value)} placeholder="Product name" className="text-xs h-8" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button data-testid="bulk-step1-next-btn" onClick={handleStep1Submit} disabled={loading || products.length === 0} className="bg-violet-600 hover:bg-violet-700">
                    {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Next: Select Model ({products.length} products)
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Select Model */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <User className="w-5 h-5 text-violet-600" />
                  Step 2: Select AI Model (applies to all {products.length} products)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Gender</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={modelFilters.gender} onChange={(e) => setModelFilters(prev => ({ ...prev, gender: e.target.value }))}>
                      <option value="">All</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="kid">Kid</option>
                    </select>
                  </div>
                  <div>
                    <Label>Age Group</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={modelFilters.age_group} onChange={(e) => setModelFilters(prev => ({ ...prev, age_group: e.target.value }))}>
                      <option value="">All</option>
                      <option value="kids">Kids</option>
                      <option value="18-25">18-25</option>
                      <option value="26-35">26-35</option>
                      <option value="36-45">36-45</option>
                    </select>
                  </div>
                  <div>
                    <Label>Complexion</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={modelFilters.complexion} onChange={(e) => setModelFilters(prev => ({ ...prev, complexion: e.target.value }))}>
                      <option value="">All</option>
                      <option value="fair">Fair</option>
                      <option value="wheatish">Wheatish</option>
                      <option value="dusky">Dusky</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>
                  <div>
                    <Label>Hair</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={modelFilters.hair} onChange={(e) => setModelFilters(prev => ({ ...prev, hair: e.target.value }))}>
                      <option value="">All</option>
                      <option value="short">Short</option>
                      <option value="long">Long</option>
                      <option value="curly">Curly</option>
                      <option value="straight">Straight</option>
                    </select>
                  </div>
                </div>

                {/* Model Grid */}
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {models.map((model) => (
                      <div key={model.model_id} onClick={() => setSelectedModel(model)} className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${selectedModel?.model_id === model.model_id ? 'border-violet-600 ring-2 ring-violet-600/20' : 'border-transparent hover:border-violet-200'}`}>
                        <img src={model.preview_image_url} alt={model.name} className="w-full aspect-[3/4] object-cover" />
                        <div className="p-3 bg-white">
                          <p className="font-medium text-neutral-900">{model.name}</p>
                          <p className="text-xs text-neutral-500 capitalize">{model.gender} • {model.age_group}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button data-testid="bulk-step2-next-btn" onClick={handleStep2Submit} className="bg-violet-600 hover:bg-violet-700">Next: Select Background<ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Background & Pose */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Palette className="w-5 h-5 text-violet-600" />
                  Step 3: Background & Poses (applies to all products)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Background Category</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bgCategories.map((cat) => (
                      <Button key={cat.id} type="button" variant={bgCategory === cat.id ? "default" : "outline"} size="sm" onClick={() => setBgCategory(cat.id)} className={bgCategory === cat.id ? "bg-violet-600" : ""}>{cat.label}</Button>
                    ))}
                  </div>
                </div>

                {loading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-violet-600 animate-spin" /></div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {backgrounds.map((bg) => (
                      <div key={bg.background_id} onClick={() => setSelectedBackground(bg)} className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${selectedBackground?.background_id === bg.background_id ? 'border-violet-600 ring-2 ring-violet-600/20' : 'border-transparent hover:border-violet-200'}`}>
                        <img src={bg.preview_image_url} alt={bg.name} className="w-full aspect-video object-cover" />
                        <div className="p-3 bg-white">
                          <p className="font-medium text-neutral-900 text-sm">{bg.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <Label>Select Poses</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {poses.map((pose) => (
                      <div key={pose.id} onClick={() => togglePose(pose.id)} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedPoses.includes(pose.id) ? 'border-violet-600 bg-violet-50' : 'border-neutral-200 hover:border-violet-200'}`}>
                        <Checkbox checked={selectedPoses.includes(pose.id)} />
                        <span className="font-medium">{pose.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div onClick={() => setVideoRequired(!videoRequired)} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${videoRequired ? 'border-violet-600 bg-violet-50' : 'border-neutral-200 hover:border-violet-200'}`}>
                  <Checkbox checked={videoRequired} />
                  <div>
                    <span className="font-medium">Generate Walking Video</span>
                    <p className="text-sm text-neutral-500">4-6 second walking/rotation video for each product</p>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button data-testid="bulk-step3-next-btn" onClick={handleStep3Submit} className="bg-violet-600 hover:bg-violet-700">Next: Review<ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 4: Review & Generate */}
          {step === 4 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                  Step 4: Review & Generate Bulk Shoot
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary */}
                <div className="bg-violet-50 rounded-xl p-6">
                  <h3 className="font-semibold text-violet-900 mb-4 flex items-center gap-2">
                    <Layers className="w-5 h-5" />
                    Bulk Generation Summary
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-3xl font-bold text-violet-600">{products.length}</p>
                      <p className="text-sm text-neutral-600">Products</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-3xl font-bold text-violet-600">{products.length * selectedPoses.length}</p>
                      <p className="text-sm text-neutral-600">Total Images</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-3xl font-bold text-violet-600">{videoRequired ? products.length : 0}</p>
                      <p className="text-sm text-neutral-600">Videos</p>
                    </div>
                  </div>
                </div>

                {/* Settings Preview */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-neutral-500">AI Model</Label>
                    <div className="flex items-center gap-3 bg-neutral-50 rounded-xl p-3">
                      <img src={selectedModel?.preview_image_url} alt={selectedModel?.name} className="w-16 h-16 rounded-lg object-cover" />
                      <div>
                        <p className="font-medium">{selectedModel?.name}</p>
                        <p className="text-sm text-neutral-500 capitalize">{selectedModel?.gender} • {selectedModel?.age_group}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-500">Background</Label>
                    <div className="flex items-center gap-3 bg-neutral-50 rounded-xl p-3">
                      <img src={selectedBackground?.preview_image_url} alt={selectedBackground?.name} className="w-16 h-10 rounded-lg object-cover" />
                      <div>
                        <p className="font-medium">{selectedBackground?.name}</p>
                        <p className="text-sm text-neutral-500 capitalize">{selectedBackground?.category}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Products Preview */}
                <div>
                  <Label className="text-neutral-500 mb-2 block">Products to Process</Label>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {products.map((product) => (
                      <div key={product.id} className="flex-shrink-0 w-20">
                        <img src={product.image_url.startsWith('/') ? `${window.location.origin}${product.image_url}` : product.image_url} alt={product.name} className="w-20 h-20 rounded-lg object-cover" />
                        <p className="text-xs text-neutral-600 truncate mt-1">{product.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div className="flex flex-wrap gap-2">
                  {selectedPoses.map(pose => (
                    <span key={pose} className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">{pose.replace('_', ' ')}</span>
                  ))}
                  {videoRequired && <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">Walking Videos</span>}
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button data-testid="bulk-generate-btn" onClick={handleFinalSubmit} disabled={submitting} className="bg-violet-600 hover:bg-violet-700">
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processing...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />Generate {products.length} AI Shoots</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BulkUpload;
